import React from "react";
import { 
  Map, 
  PriorityHigh, 
  Unpublished, 
  Equalizer,
  Public,
  Category,
  AccessTime,
  TrendingUp,
  CreditCard,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Typography,
  useTheme,
  Chip,
  Fade,
  CardContent,
  Avatar
} from "@mui/material";
import { useGetMapsQuery } from "api/api-maps";
import { 
  useGetRiskCategoryExposureQuery,
  useGetInsurerExposureQuery,
  useGetCountryExposureQuery,
  useGetIndustryExposureQuery,
  useGetInvoiceOverdueDaysQuery,
  useGetOutstandingCreditLimitQuery
} from "api/api-risk-dashboard";
import { INCOMLEND_INTERNAL_LINK, MAPS } from "codes";
import history from "helpers/history";
import useIsMobile from "hooks/useIsMobile";
import moment from "moment";
import CountryExposureBarChart from "./TotalCreditExposure/CountryExposureBarChart";
import IndustryExposureBarChart from "./TotalCreditExposure/IndustryExposureBarChart";
import ProductBasedRiskBarChart from "./TotalCreditExposure/ProductBasedRiskBarChart";
import ClientCreditLimitBarChart from "./TotalCreditExposure/ClientCreditLimitBarChart";
import ExposureTabbedCard from "./TotalCreditExposure/ExposureTabbedCard";
import MonitoringDatesSummary from "./MonitoringDatesSummary";
import AgingAnalysisPieChart from "./TotalCreditExposure/AgingAnalysisPieChart";
import {
  DashboardWrapper,
  DashboardGrid,
  StyledCard,
  ChartContainer,
  MetricCard,
  ChipContainer,
  GradientDivider,
  CardHeader
} from "./styles";

// Define our card object type to match exactly what we use
type CardData = {
  title: string;
  subtitle: string;
  onClick: () => void;
  backgroundColor: string;
  horizontal: boolean;
  headingProps: any;
  borderRadius: string;
  titleContainerSpacing: number;
  sidebarChildren: React.ReactNode;
  bottomColorBand: string;
  height: number;
  isFetching: boolean;
  id: string;
};

const RiskManagementDashboard: React.FC = () => {
  const isMobile = useIsMobile();
  const theme = useTheme();

  const mapparams = "";
  const { data: maps, isFetching: isMapsFetching } = useGetMapsQuery({ mapparams });
  
  // API calls for exposure data
  const { data: riskCategoryData, isLoading: isRiskCategoryLoading, error: riskCategoryError } = useGetRiskCategoryExposureQuery();
  const { data: insurerData, isLoading: isInsurerLoading, error: insurerError } = useGetInsurerExposureQuery();
  const { data: countryData, isLoading: isCountryLoading, error: countryError } = useGetCountryExposureQuery();
  const { data: industryData, isLoading: isIndustryLoading, error: industryError } = useGetIndustryExposureQuery();
  const { data: agingData, isLoading: isAgingLoading, error: agingError } = useGetInvoiceOverdueDaysQuery();
  const { data: clientCreditLimitData, isLoading: isClientCreditLimitLoading, error: clientCreditLimitError } = useGetOutstandingCreditLimitQuery();

  const renderMetricCard = (icon: React.ReactNode, title: string, value: string | number, color: string) => (
    <MetricCard elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main` }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" color="textSecondary">
            {title}
          </Typography>
          <Typography variant="h6" color={`${color}.main`} sx={{ fontWeight: 'bold' }}>
            {value}
          </Typography>
        </Box>
      </Box>
    </MetricCard>
  );

  const overviewData: CardData[] = [
    {
      title: "Supplier-Buyer Maps",
      subtitle: `${maps?.pagination?.total ?? 0} maps`,
      onClick: () => history.push(`${INCOMLEND_INTERNAL_LINK}${MAPS}`),
      backgroundColor: theme.palette.background.default,
      horizontal: true,
      headingProps: {
        variant: "body1",
        fontWeight: "bold",
        fontSize: "0.9rem",
      },
      borderRadius: "8px",
      titleContainerSpacing: 0,
      sidebarChildren: <Map fontSize="small" />,
      bottomColorBand: `3px solid ${theme.palette.primary.main}`,
      height: isMobile ? 60 : 80,
      isFetching: isMapsFetching,
      id: "supplier-buyer-maps"
    },
    {
      title: "High Risk Maps",
      subtitle: `${maps?.maps_details?.filter((map: any) => map.score?.toLowerCase() === "high")?.length ?? 0} maps with high risk`,
      onClick: () => history.push(`${INCOMLEND_INTERNAL_LINK}${MAPS}`),
      backgroundColor: theme.palette.background.default,
      horizontal: true,
      headingProps: {
        variant: "body1",
        fontWeight: "bold",
        fontSize: "0.9rem",
      },
      borderRadius: "8px",
      titleContainerSpacing: 0,
      sidebarChildren: <PriorityHigh fontSize="small" />,
      bottomColorBand: `3px solid ${theme.palette.error.main}`,
      height: isMobile ? 60 : 80,
      isFetching: isMapsFetching,
      id: "high-risk-maps"
    },
    {
      title: "Incomplete Maps",
      subtitle: `${maps?.maps_details?.filter((map: any) => !map.score)?.length ?? 0} of ${maps?.maps_details?.length ?? 0} incomplete`,
      onClick: () => history.push(`${INCOMLEND_INTERNAL_LINK}${MAPS}`),
      backgroundColor: theme.palette.background.default,
      horizontal: true,
      headingProps: {
        variant: "body1",
        fontWeight: "bold",
        fontSize: "0.9rem",
      },
      borderRadius: "8px",
      titleContainerSpacing: 0,
      sidebarChildren: <Unpublished fontSize="small" />,
      bottomColorBand: `3px solid ${theme.palette.secondary.main}`,
      height: isMobile ? 60 : 80,
      isFetching: isMapsFetching,
      id: "incomplete-maps"
    },
  ];

  const overDue = maps?.maps_details?.filter(
    (map: any) =>
      map.score?.toLowerCase() === "high" &&
      moment(map.next_monitoring_date).isBefore(moment()) &&
      moment(map.next_monitoring_date).isAfter(moment().subtract(30, "days"))
  );

  const dueInAMonth = maps?.maps_details?.filter(
    (map: any) =>
      map.score?.toLowerCase() === "high" &&
      moment(map.next_monitoring_date).isBefore(
        moment().subtract(30, "days")
      ) &&
      moment(map.next_monitoring_date).isAfter(moment())
  );
  const upcomingHighRiskMaps = maps?.maps_details?.filter(
    (map: any) =>
      map.score?.toLowerCase() === "high" &&
      moment(map.next_monitoring_date).isAfter(moment().add(30, "days"))
  );

  return (
    <DashboardWrapper>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Risk Management Dashboard
      </Typography>
      <GradientDivider />
      
      <DashboardGrid container spacing={2}>
        {/* Overview Metrics */}
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            {renderMetricCard(
              <Map fontSize="small" />,
              "Total Maps",
              maps?.pagination?.total ?? 0,
              "primary"
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderMetricCard(
              <PriorityHigh fontSize="small" />,
              "High Risk Maps",
              maps?.maps_details?.filter((map: any) => map.score?.toLowerCase() === "high")?.length ?? 0,
              "error"
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {renderMetricCard(
              <Unpublished fontSize="small" />,
              "Incomplete Maps",
              maps?.maps_details?.filter((map: any) => !map.score)?.length ?? 0,
              "warning"
            )}
          </Grid>
        </Grid>

        {/* Risk Category and Insurer Exposure */}
        <Grid item xs={12}>
          <Fade in timeout={500}>
            <StyledCard>
              <ExposureTabbedCard
                riskCategoryData={riskCategoryData as any}
                isRiskCategoryLoading={isRiskCategoryLoading}
                riskCategoryError={riskCategoryError}
                insurerData={insurerData as any}
                isInsurerLoading={isInsurerLoading}
                insurerError={insurerError}
                kycData={null}
                isKycLoading={false}
                kycError={null}
              />
            </StyledCard>
          </Fade>
        </Grid>

        {/* Country and Industry Exposure */}
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={700}>
              <StyledCard>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Public fontSize="small" />
                      <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.01em', fontSize: '1.1rem' }}>
                        Country Exposure
                      </Typography>
                    </Box>
                  }
                  action={
                    <ChipContainer>
                      <Chip 
                        label="Top: India (32%)" 
                        size="small" 
                        color="primary"
                      />
                      <Chip 
                        label="High Risk: 4" 
                        size="small" 
                        color="error"
                      />
                    </ChipContainer>
                  }
                />
                <CardContent>
                  <ChartContainer>
                    <CountryExposureBarChart 
                      countryData={countryData as any}
                      isLoading={isCountryLoading}
                      error={countryError}
                    />
                  </ChartContainer>
                </CardContent>
              </StyledCard>
            </Fade>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Fade in timeout={900}>
              <StyledCard>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Equalizer fontSize="small" />
                      <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.01em', fontSize: '1.1rem' }}>
                        Industry Exposure
                      </Typography>
                    </Box>
                  }
                  action={
                    <ChipContainer>
                      <Chip 
                        label="Top: Tech (28%)" 
                        size="small" 
                        color="primary"
                      />
                      <Chip 
                        label="Avg: ₹42.5L" 
                        size="small" 
                        color="info"
                      />
                    </ChipContainer>
                  }
                />
                <CardContent>
                  <ChartContainer>
                    <IndustryExposureBarChart 
                      industryData={industryData as any}
                      isLoading={isIndustryLoading}
                      error={industryError}
                    />
                  </ChartContainer>
                </CardContent>
              </StyledCard>
            </Fade>
          </Grid>
        </Grid>

        {/* Client Credit Limit Exposure - New Card */}
        <Grid item xs={12}>
          <Fade in timeout={1000}>
            <StyledCard>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCard fontSize="small" />
                    <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.01em', fontSize: '1.1rem' }}>
                      Top 10 Client Credit Limits
                    </Typography>
                  </Box>
                }
                action={
                  clientCreditLimitData && clientCreditLimitData.data && clientCreditLimitData.data.length > 0 ? (
                    <ChipContainer>
                      <Chip 
                        label={`Top Client: ${clientCreditLimitData.data[0]?.buyer_name || 'N/A'}`}
                        size="small" 
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      />
                      <Chip 
                        label="Credit Utilization" 
                        size="small" 
                        color="warning"
                        sx={{ fontWeight: 500 }}
                      />
                    </ChipContainer>
                  ) : null
                }
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, px: 1 }}>
                  Showing approved credit limits and outstanding amounts for the top clients by total credit limit
                </Typography>
                <ChartContainer sx={{ height: 400 }}>
                  <ClientCreditLimitBarChart 
                    creditLimitData={clientCreditLimitData}
                    isLoading={isClientCreditLimitLoading}
                    error={clientCreditLimitError}
                  />
                </ChartContainer>
              </CardContent>
            </StyledCard>
          </Fade>
        </Grid>

        {/* Product Based Risk and Aging Analysis */}
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={1100}>
              <StyledCard>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Category fontSize="small" />
                      <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.01em', fontSize: '1.1rem' }}>
                        Product Based Risk
                      </Typography>
                    </Box>
                  }
                  action={
                    <ChipContainer>
                      <Chip 
                        label="High: Invoice Financing (42%)" 
                        size="small" 
                        color="error"
                      />
                      <Chip 
                        label="Low: Supply Chain (18%)" 
                        size="small" 
                        color="success"
                      />
                    </ChipContainer>
                  }
                />
                <CardContent>
                  <ChartContainer>
                    <ProductBasedRiskBarChart />
                  </ChartContainer>
                </CardContent>
              </StyledCard>
            </Fade>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Fade in timeout={1300}>
              <StyledCard>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime fontSize="small" />
                      <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.01em', fontSize: '1.1rem' }}>
                        Aging Analysis
                      </Typography>
                    </Box>
                  }
                  action={
                    <ChipContainer>
                      <Chip 
                        label={`Total Overdues: ${agingData?.data?.reduce((acc: number, curr: any) => acc + curr.count, 0) || 0}`}
                        size="small" 
                        color="primary"
                      />
                    </ChipContainer>
                  }
                />
                <CardContent>
                  <ChartContainer>
                    <AgingAnalysisPieChart 
                      agingData={agingData}
                      isLoading={isAgingLoading}
                      error={agingError}
                      showTitle={false}
                    />
                  </ChartContainer>
                </CardContent>
              </StyledCard>
            </Fade>
          </Grid>
        </Grid>

        {/* Monitoring Dates */}
        <Grid item xs={12}>
          <Fade in timeout={1500}>
            <StyledCard>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp fontSize="small" />
                    <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.01em', fontSize: '1.1rem' }}>
                      Monitoring Schedule
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <MonitoringDatesSummary
                  overDue={overDue}
                  dueInAMonth={dueInAMonth}
                  upcomingHighRiskMaps={upcomingHighRiskMaps}
                />
              </CardContent>
            </StyledCard>
          </Fade>
        </Grid>
      </DashboardGrid>
    </DashboardWrapper>
  );
};

export default RiskManagementDashboard;
